/**
 * MULTI Adjust# column catalog for BalanceSheet / ProfitLoss.
 * @see docs/multi-year/fsadjustment-multi-adj-yoa-column-gap.md §4.2
 */

export type MultiAdjStatementTouch = 'BS' | 'PL';

export type MultiAdjColumnMeta = {
    headerId?: string;
    refNo: string;
    isAutoAdj: boolean;
    autoAdjType?: string;
    yearOfAssessment?: string | null;
    touchesBs: boolean;
    touchesPl: boolean;
};

type WalkPaper = {
    headerId?: string | null;
    refNo?: string | null;
    isAutoAdj?: boolean;
    adjustmentMode?: number;
    autoAdjType?: string | null;
    yearOfAssessment?: string | null;
};

const AUTO_ADJ_TYPE_ORDER = [
    'DIR_DTD',
    'LOAN_BANK',
    'LOAN_VEHICLE',
    'GL_DISPOSAL',
    'GL_EXCHANGE',
    'TAX',
    'TAX_EXCHANGE',
] as const;

function isAutoAdjPaper(adj: WalkPaper): boolean {
    return adj?.isAutoAdj === true || adj?.adjustmentMode === 4;
}

/**
 * Match an auto paper to an Adjust#(Auto) column.
 * Prefer autoAdjType when present so TAX and TAX_EXCHANGE never share a column
 * (refNo fallback only for legacy papers missing autoAdjType).
 */
export function matchesAutoAdjColumn(
    adj: {autoAdjType?: string | null; refNo?: string | number | null} | null | undefined,
    autoType: string,
    autoRefNo?: string,
): boolean {
    const type = String(autoType || '').trim();
    const adjType = String(adj?.autoAdjType || '').trim();
    if (type && adjType) {
        return adjType === type;
    }
    return String(adj?.refNo ?? '') === String(autoRefNo ?? '');
}

function columnKey(col: Pick<MultiAdjColumnMeta, 'headerId' | 'refNo' | 'isAutoAdj' | 'autoAdjType' | 'yearOfAssessment'>): string {
    if (col.isAutoAdj) {
        const yoa = String(col.yearOfAssessment || '').trim();
        // MULTI：同 type 多年 header 必须分列，否则第二年 tab 只剩空列
        return yoa
            ? `auto:${col.autoAdjType || col.refNo}:${yoa}`
            : `auto:${col.autoAdjType || col.refNo}`;
    }
    if (col.headerId != null && String(col.headerId).trim() !== '') {
        return `h:${col.headerId}`;
    }
    return `r:${col.refNo}:manual`;
}

function walkTree(
    nodes: any[] | null | undefined,
    touch: MultiAdjStatementTouch,
    into: Map<string, MultiAdjColumnMeta>,
) {
    for (const node of nodes || []) {
        if (Array.isArray(node?.adjustment)) {
            for (const adj of node.adjustment as WalkPaper[]) {
                const refNo = String(adj?.refNo || '').trim();
                if (!refNo) continue;
                const isAutoAdj = isAutoAdjPaper(adj);
                const autoAdjType = adj?.autoAdjType ? String(adj.autoAdjType) : undefined;
                const headerId = adj?.headerId != null && String(adj.headerId).trim() !== ''
                    ? String(adj.headerId)
                    : undefined;
                const yoa = adj?.yearOfAssessment != null
                    ? String(adj.yearOfAssessment).trim() || null
                    : null;
                const meta: MultiAdjColumnMeta = {
                    headerId,
                    refNo,
                    isAutoAdj,
                    autoAdjType,
                    yearOfAssessment: yoa,
                    touchesBs: touch === 'BS',
                    touchesPl: touch === 'PL',
                };
                const key = columnKey(meta);
                const existing = into.get(key);
                if (!existing) {
                    into.set(key, meta);
                } else {
                    existing.touchesBs = existing.touchesBs || meta.touchesBs;
                    existing.touchesPl = existing.touchesPl || meta.touchesPl;
                    if (!existing.headerId && meta.headerId) existing.headerId = meta.headerId;
                    if (!existing.yearOfAssessment && meta.yearOfAssessment) {
                        existing.yearOfAssessment = meta.yearOfAssessment;
                    }
                    if (!existing.autoAdjType && meta.autoAdjType) {
                        existing.autoAdjType = meta.autoAdjType;
                    }
                }
            }
        }
        if (Array.isArray(node?.children) && node.children.length > 0) {
            walkTree(node.children, touch, into);
        }
    }
}

function sortCatalog(cols: MultiAdjColumnMeta[]): MultiAdjColumnMeta[] {
    return [...cols].sort((a, b) => {
        if (a.isAutoAdj !== b.isAutoAdj) return a.isAutoAdj ? 1 : -1;
        if (a.isAutoAdj && b.isAutoAdj) {
            const ia = AUTO_ADJ_TYPE_ORDER.indexOf(a.autoAdjType as typeof AUTO_ADJ_TYPE_ORDER[number]);
            const ib = AUTO_ADJ_TYPE_ORDER.indexOf(b.autoAdjType as typeof AUTO_ADJ_TYPE_ORDER[number]);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        }
        const na = parseFloat(a.refNo) || 0;
        const nb = parseFloat(b.refNo) || 0;
        if (na !== nb) return na - nb;
        return String(a.refNo).localeCompare(String(b.refNo));
    });
}

/**
 * Build header-level Adjust# catalog from BS + engagement PL + all period PL trees.
 */
export function collectMultiAdjColumnCatalog(args: {
    bsLine?: any[] | null;
    plLine?: any[] | null;
    periods?: Array<{yearOfAssessment?: string | null; plLine?: any[] | null} | null> | null;
}): MultiAdjColumnMeta[] {
    const map = new Map<string, MultiAdjColumnMeta>();
    walkTree(args.bsLine, 'BS', map);
    walkTree(args.plLine, 'PL', map);
    for (const period of args.periods || []) {
        if (period?.plLine && Array.isArray(period.plLine) && period.plLine.length > 0) {
            walkTree(period.plLine, 'PL', map);
        }
    }
    return sortCatalog(Array.from(map.values()));
}

/** Manual refNos for PL column headers (shared order with merge). */
export function manualRefNosFromCatalog(
    catalog: MultiAdjColumnMeta[],
    filter?: (col: MultiAdjColumnMeta) => boolean,
): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const col of catalog) {
        if (col.isAutoAdj) continue;
        if (filter && !filter(col)) continue;
        if (seen.has(col.refNo)) continue;
        seen.add(col.refNo);
        out.push(col.refNo);
    }
    return out;
}

/** Auto columns for PL (type + refNo), catalog order. Deduped per type after filter. */
export function autoAdjItemsFromCatalog(
    catalog: MultiAdjColumnMeta[],
    filter?: (col: MultiAdjColumnMeta) => boolean,
): Array<{type: string; refNo: string}> {
    const out: Array<{type: string; refNo: string}> = [];
    const seen = new Set<string>();
    for (const col of catalog) {
        if (!col.isAutoAdj) continue;
        if (filter && !filter(col)) continue;
        // Dedupe by type after filter: single-year tab already YOA-filtered;
        // merge/total view keeps one Adjust#(Auto) column per type.
        const typeKey = col.autoAdjType || col.refNo;
        if (seen.has(typeKey)) continue;
        seen.add(typeKey);
        out.push({type: typeKey, refNo: col.refNo});
    }
    return out;
}

/**
 * PL single (multiple view): only columns that touch PL for this YOA tab.
 * Papers without YOA still included when touchesPl (legacy).
 */
export function plSingleColumnFilter(
    col: MultiAdjColumnMeta,
    tabYoa: string,
): boolean {
    if (!col.touchesPl) return false;
    const yoa = String(col.yearOfAssessment || '').trim();
    if (!yoa) return true;
    return yoa === String(tabYoa || '').trim();
}

/**
 * PL merge (total view): all BS-touching (empty cells) + all PL-touching columns.
 */
export function plMergeColumnFilter(col: MultiAdjColumnMeta): boolean {
    return col.touchesBs || col.touchesPl;
}